import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/account";

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  return (
    <div data-testid="login-page" className="min-h-screen bg-bone pt-[72px] flex items-center justify-center px-5">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md text-center"
      >
        <div className="w-16 h-16 rounded-full bg-sage flex items-center justify-center mx-auto">
          <Sparkles className="w-7 h-7 text-forest" strokeWidth={1.25} />
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight text-ink mt-6 leading-[1.05]">
          Welcome to Sable.
        </h1>
        <p className="text-stone mt-4 leading-relaxed">
          Sign in to build your protocol, check out securely, and track every order.
        </p>

        <button
          data-testid="google-login-btn"
          onClick={() => login(from)}
          className="mt-8 w-full flex items-center justify-center gap-3 rounded-full bg-white border border-line py-4 text-base font-semibold text-ink hover:bg-sand active:scale-[0.99] transition-[background-color,transform] duration-200 shadow-sm"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </button>

        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-stone">
          <ShieldCheck className="w-4 h-4 text-forest" strokeWidth={1.5} />
          Secured by Google. We never see your password.
        </p>
      </motion.div>
    </div>
  );
}
